import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Bug, Lightbulb, Bell, CheckCircle, Clock, AlertTriangle,
  ArrowUp, Server, Database, Wifi, WifiOff, Users, ChevronDown,
  MessageSquare, LogOut, RefreshCw, Filter, Send, ChevronRight,
  Zap, Shield, Package, Circle, ExternalLink, XCircle, GitCommit, Mail,
  FileText,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import StabilisationLog from '../../components/admin/StabilisationLog';

// ─── Shared Components ─────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const config = {
    open: { bg: '#FEF3C7', color: '#92400E', label: 'Open' },
    in_progress: { bg: '#DBEAFE', color: '#1E40AF', label: 'In Progress' },
    resolved: { bg: '#D1FAE5', color: '#065F46', label: 'Resolved' },
    closed: { bg: '#F3F4F6', color: '#374151', label: 'Closed' },
  };
  const c = config[status] || config.open;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
};

const PriorityBadge = ({ priority, escalated }) => {
  const config = {
    low: { bg: '#F3F4F6', color: '#6B7280' },
    medium: { bg: '#FEF3C7', color: '#92400E' },
    high: { bg: '#FEE2E2', color: '#991B1B' },
    critical: { bg: '#DC2626', color: '#FFFFFF' },
  };
  const c = config[priority] || config.medium;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1" style={{ backgroundColor: c.bg, color: c.color }}>
      {escalated && <ArrowUp size={10} />}
      {priority?.charAt(0).toUpperCase() + priority?.slice(1)}
    </span>
  );
};

const TypeIcon = ({ type }) => type === 'bug'
  ? <Bug size={14} style={{ color: '#DC2626' }} />
  : <Lightbulb size={14} style={{ color: '#F59E0B' }} />;

const SlaBadge = ({ sla }) => {
  if (!sla || sla.sla_status === 'met') return null;
  const config = {
    on_track: { bg: '#D1FAE5', color: '#065F46', label: 'SLA OK' },
    at_risk: { bg: '#FEF3C7', color: '#92400E', label: 'SLA At Risk' },
    breached: { bg: '#DC2626', color: '#FFFFFF', label: 'SLA Breached' },
  };
  const c = config[sla.sla_status] || config.on_track;
  const timeLabel = sla.sla_breached
    ? `${Math.abs(sla.sla_hours_remaining)}h overdue`
    : sla.sla_hours_remaining < 24
      ? `${sla.sla_hours_remaining}h left`
      : `${Math.floor(sla.sla_hours_remaining / 24)}d left`;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1" style={{ backgroundColor: c.bg, color: c.color }}>
      <Clock size={10} />
      {c.label} &middot; {timeLabel}
    </span>
  );
};

const SlaProgressBar = ({ sla }) => {
  if (!sla || sla.sla_status === 'met') return null;
  const pct = Math.min(sla.sla_percent_used || 0, 100);
  const color = sla.sla_breached ? '#DC2626' : sla.sla_status === 'at_risk' ? '#F59E0B' : '#2EAA5C';
  return (
    <div className="w-full h-1.5 rounded-full mt-2" style={{ backgroundColor: '#F3F4F6' }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

// ─── System Health Tab ──────────────────────────────────────────────────────

const SystemHealthTab = ({ health, loading, onRefresh, onSendDigest, digestSending }) => {
  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw size={24} className="animate-spin" style={{ color: '#F5831F' }} /></div>;
  if (!health) return <p className="text-center py-10 text-gray-400">No data available</p>;

  const integrationStatus = (status) => {
    if (status === 'configured') return <CheckCircle size={16} style={{ color: '#2EAA5C' }} />;
    return <WifiOff size={16} style={{ color: '#DC2626' }} />;
  };

  const workerStatus = (w) => {
    if (!w || w.status === 'no_runs') return <Circle size={10} style={{ color: '#9CA3AF' }} />;
    if (w.status === 'success' || w.status === 'completed') return <Circle size={10} fill="#2EAA5C" style={{ color: '#2EAA5C' }} />;
    return <Circle size={10} fill="#F59E0B" style={{ color: '#F59E0B' }} />;
  };

  return (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>Active Users (24h)</p>
              <p className="text-3xl font-bold mt-1" style={{ color: '#1A1209' }}>{health.active_users?.last_24h || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F15' }}>
              <Users size={20} style={{ color: '#F5831F' }} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>Active (7d)</p>
              <p className="text-3xl font-bold mt-1" style={{ color: '#1A1209' }}>{health.active_users?.last_7d || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2563EB15' }}>
              <Activity size={20} style={{ color: '#2563EB' }} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>Open Tickets</p>
              <p className="text-3xl font-bold mt-1" style={{ color: '#1A1209' }}>{health.tickets?.open || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F59E0B15' }}>
              <Bug size={20} style={{ color: '#F59E0B' }} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>Escalated</p>
              <p className="text-3xl font-bold mt-1" style={{ color: health.tickets?.escalated > 0 ? '#DC2626' : '#1A1209' }}>{health.tickets?.escalated || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#DC262615' }}>
              <AlertTriangle size={20} style={{ color: '#DC2626' }} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: `1px solid ${(health.tickets?.sla_breached || 0) > 0 ? '#FCA5A5' : '#E0E0E0'}` }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>SLA Breached</p>
              <p className="text-3xl font-bold mt-1" style={{ color: (health.tickets?.sla_breached || 0) > 0 ? '#DC2626' : '#2EAA5C' }}>{health.tickets?.sla_breached || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: (health.tickets?.sla_breached || 0) > 0 ? '#DC262615' : '#2EAA5C15' }}>
              <Clock size={20} style={{ color: (health.tickets?.sla_breached || 0) > 0 ? '#DC2626' : '#2EAA5C' }} />
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: `1px solid ${(health.tickets?.sla_at_risk || 0) > 0 ? '#FDE68A' : '#E0E0E0'}` }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs" style={{ color: '#666' }}>SLA At Risk</p>
              <p className="text-3xl font-bold mt-1" style={{ color: (health.tickets?.sla_at_risk || 0) > 0 ? '#F59E0B' : '#2EAA5C' }}>{health.tickets?.sla_at_risk || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: (health.tickets?.sla_at_risk || 0) > 0 ? '#F59E0B15' : '#2EAA5C15' }}>
              <AlertTriangle size={20} style={{ color: (health.tickets?.sla_at_risk || 0) > 0 ? '#F59E0B' : '#2EAA5C' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DB Collections */}
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4">
            <Database size={18} style={{ color: '#F5831F' }} />
            <h3 className="font-semibold" style={{ color: '#1A1209' }}>Database Collections</h3>
          </div>
          <div className="space-y-2">
            {health.database && Object.entries(health.database).map(([col, count]) => (
              <div key={col} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                <span className="text-sm" style={{ color: '#333' }}>{col}</span>
                <span className="text-sm font-mono font-medium" style={{ color: count >= 0 ? '#1A1209' : '#DC2626' }}>
                  {count >= 0 ? count.toLocaleString() : 'Error'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Integrations */}
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4">
            <Wifi size={18} style={{ color: '#F5831F' }} />
            <h3 className="font-semibold" style={{ color: '#1A1209' }}>Integrations</h3>
          </div>
          <div className="space-y-2">
            {health.integrations && Object.entries(health.integrations).map(([name, status]) => (
              <div key={name} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                <span className="text-sm capitalize" style={{ color: '#333' }}>{name}</span>
                <div className="flex items-center gap-2">
                  {integrationStatus(status)}
                  <span className="text-xs" style={{ color: status === 'configured' ? '#2EAA5C' : '#DC2626' }}>
                    {status === 'configured' ? 'Active' : 'Not Configured'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Background Workers */}
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4">
            <Zap size={18} style={{ color: '#F5831F' }} />
            <h3 className="font-semibold" style={{ color: '#1A1209' }}>Background Workers</h3>
          </div>
          <div className="space-y-3">
            {health.workers && Object.entries(health.workers).map(([name, info]) => (
              <div key={name} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  {workerStatus(info)}
                  <span className="text-sm" style={{ color: '#333' }}>{name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                </div>
                <span className="text-xs" style={{ color: '#999' }}>
                  {info?.last_run ? new Date(info.last_run).toLocaleString() : 'Never run'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Errors */}
        <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} style={{ color: '#DC2626' }} />
            <h3 className="font-semibold" style={{ color: '#1A1209' }}>Recent Error Logs</h3>
          </div>
          {health.recent_errors?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {health.recent_errors.map((err, i) => (
                <div key={i} className="p-2 rounded text-xs" style={{ backgroundColor: '#FEF2F2' }}>
                  <p className="font-medium" style={{ color: '#991B1B' }}>{err.action}</p>
                  <p className="mt-0.5" style={{ color: '#999' }}>{new Date(err.timestamp).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-center py-6" style={{ color: '#999' }}>No recent errors</p>
          )}
        </div>
      </div>

      {/* Weekly Digest */}
      <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={18} style={{ color: '#F5831F' }} />
            <div>
              <h3 className="font-semibold" style={{ color: '#1A1209' }}>Weekly Digest</h3>
              <p className="text-xs" style={{ color: '#999' }}>Auto-sent every Monday 8 AM UTC</p>
            </div>
          </div>
          <button
            onClick={onSendDigest}
            disabled={digestSending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 transition-all"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="send-digest-btn"
          >
            <Send size={14} />
            {digestSending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-2 rounded" style={{ backgroundColor: '#F5831F10' }}>
            <p className="text-lg font-bold" style={{ color: '#F5831F' }}>{health.tickets?.open || 0}</p>
            <p className="text-xs" style={{ color: '#999' }}>Open</p>
          </div>
          <div className="text-center p-2 rounded" style={{ backgroundColor: '#2EAA5C10' }}>
            <p className="text-lg font-bold" style={{ color: '#2EAA5C' }}>
              {Object.values(health.workers || {}).filter(w => w?.status === 'completed' || w?.status === 'success').length}
            </p>
            <p className="text-xs" style={{ color: '#999' }}>Workers OK</p>
          </div>
          <div className="text-center p-2 rounded" style={{ backgroundColor: '#2563EB10' }}>
            <p className="text-lg font-bold" style={{ color: '#2563EB' }}>{health.active_users?.last_7d || 0}</p>
            <p className="text-xs" style={{ color: '#999' }}>Active 7d</p>
          </div>
          <div className="text-center p-2 rounded" style={{ backgroundColor: '#DC262610' }}>
            <p className="text-lg font-bold" style={{ color: '#DC2626' }}>{health.tickets?.escalated || 0}</p>
            <p className="text-xs" style={{ color: '#999' }}>Escalated</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Tickets Tab ────────────────────────────────────────────────────────────

const TicketsTab = ({ tickets, stats, loading, onRefresh, onUpdateTicket, onRespond, onSearch }) => {
  const [filter, setFilter] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Trigger server-side search
  useEffect(() => {
    onSearch(debouncedSearch);
    // The parent's `onSearch` reference may change per render, but the
    // effect body is idempotent (single fire-and-forget call to the
    // parent), so re-firing on identity flips is safe — no state
    // cascade, no double-fetch (the parent handler itself owns the
    // fetch dedup).
  }, [debouncedSearch, onSearch]);

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  const handleRespond = async (ticketId) => {
    if (!responseText.trim()) return;
    setResponding(true);
    await onRespond(ticketId, responseText);
    setResponseText('');
    setResponding(false);
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    await onUpdateTicket(ticketId, { status: newStatus });
  };

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total', value: stats?.total || 0, color: '#1A1209' },
          { label: 'Open', value: stats?.open || 0, color: '#F59E0B' },
          { label: 'In Progress', value: stats?.in_progress || 0, color: '#2563EB' },
          { label: 'Resolved', value: stats?.resolved || 0, color: '#2EAA5C' },
          { label: 'Escalated', value: stats?.escalated || 0, color: '#DC2626' },
          { label: 'SLA Breached', value: stats?.sla_breached || 0, color: (stats?.sla_breached || 0) > 0 ? '#DC2626' : '#2EAA5C' },
          { label: 'SLA At Risk', value: stats?.sla_at_risk || 0, color: (stats?.sla_at_risk || 0) > 0 ? '#F59E0B' : '#2EAA5C' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg p-3 text-center" style={{ border: '1px solid #E0E0E0' }}>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs" style={{ color: '#666' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#999' }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search tickets by ID, title, description, or submitter..."
          className="w-full pl-9 pr-9 py-2 rounded-lg border text-sm bg-white"
          style={{ borderColor: '#E0E0E0' }}
          data-testid="ticket-search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <XCircle size={14} style={{ color: '#999' }} />
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} style={{ color: '#999' }} />
        {['all', 'open', 'in_progress', 'resolved', 'closed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-3 py-1 rounded-full text-xs font-medium transition-all"
            style={{
              backgroundColor: filter === f ? '#F5831F' : '#F3F4F6',
              color: filter === f ? '#FFF' : '#666',
            }}
            data-testid={`filter-${f}`}
          >
            {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Ticket List */}
      {loading ? (
        <div className="flex justify-center py-10"><RefreshCw size={24} className="animate-spin" style={{ color: '#F5831F' }} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg p-10 text-center" style={{ border: '1px solid #E0E0E0' }}>
          <CheckCircle size={40} className="mx-auto mb-3" style={{ color: '#2EAA5C' }} />
          <p className="text-sm" style={{ color: '#666' }}>No tickets to show</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(ticket => (
            <div key={ticket.ticket_id} className="bg-white rounded-lg overflow-hidden transition-all" style={{ border: `1px solid ${ticket.escalated ? '#FCA5A5' : '#E0E0E0'}` }}>
              {/* Ticket Header */}
              <button
                onClick={() => setExpandedTicket(expandedTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                data-testid={`ticket-${ticket.ticket_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <TypeIcon type={ticket.type} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs" style={{ color: '#999' }}>{ticket.ticket_id}</span>
                      <StatusBadge status={ticket.status} />
                      <PriorityBadge priority={ticket.priority} escalated={ticket.escalated} />
                      {ticket.sla && <SlaBadge sla={ticket.sla} />}
                    </div>
                    <p className="font-medium text-sm mt-1 truncate" style={{ color: '#1A1209' }}>{ticket.title}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#999' }}>
                      {ticket.submitted_by_name} &middot; {ticket.domain_context} &middot; {new Date(ticket.created_at).toLocaleDateString()}
                    </p>
                    {ticket.sla && <SlaProgressBar sla={ticket.sla} />}
                  </div>
                </div>
                <ChevronRight size={16} className={`transition-transform ${expandedTicket === ticket.ticket_id ? 'rotate-90' : ''}`} style={{ color: '#999' }} />
              </button>

              {/* Expanded Detail */}
              {expandedTicket === ticket.ticket_id && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: '#E0E0E0' }}>
                  <div className="pt-3">
                    <p className="text-sm" style={{ color: '#333' }}>{ticket.description}</p>

                    {ticket.escalated && (
                      <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#FEF2F2' }}>
                        <p className="text-xs font-medium" style={{ color: '#DC2626' }}>
                          Escalated by {ticket.escalated_by_name}: {ticket.escalation_reason}
                        </p>
                      </div>
                    )}

                    {/* Status Actions */}
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                      <span className="text-xs" style={{ color: '#666' }}>Set status:</span>
                      {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(ticket.ticket_id, s)}
                          disabled={ticket.status === s}
                          className="px-2 py-1 rounded text-xs transition-all disabled:opacity-40"
                          style={{
                            backgroundColor: ticket.status === s ? '#F5831F' : '#F3F4F6',
                            color: ticket.status === s ? '#FFF' : '#666',
                          }}
                          data-testid={`set-status-${s}-${ticket.ticket_id}`}
                        >
                          {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </button>
                      ))}
                    </div>

                    {/* Responses Thread */}
                    {ticket.responses?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-semibold" style={{ color: '#666' }}>Conversation</p>
                        {ticket.responses.map((r, i) => (
                          <div key={i} className="p-2 rounded" style={{ backgroundColor: '#F9FAFB' }}>
                            <p className="text-xs font-medium" style={{ color: '#F5831F' }}>{r.from_name}</p>
                            <p className="text-sm mt-0.5" style={{ color: '#333' }}>{r.message}</p>
                            <p className="text-xs mt-1" style={{ color: '#999' }}>{new Date(r.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Respond */}
                    <div className="mt-4 flex gap-2">
                      <input
                        type="text"
                        value={expandedTicket === ticket.ticket_id ? responseText : ''}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Type a response..."
                        className="flex-1 px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: '#E0E0E0' }}
                        data-testid={`response-input-${ticket.ticket_id}`}
                        onKeyDown={(e) => e.key === 'Enter' && handleRespond(ticket.ticket_id)}
                      />
                      <button
                        onClick={() => handleRespond(ticket.ticket_id)}
                        disabled={responding || !responseText.trim()}
                        className="px-3 py-2 rounded-lg text-white text-sm disabled:opacity-50 transition-all"
                        style={{ backgroundColor: '#F5831F' }}
                        data-testid={`respond-btn-${ticket.ticket_id}`}
                      >
                        <Send size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Notifications Tab ──────────────────────────────────────────────────────

const NotificationsTab = ({ notifications, unread, loading, onMarkAllRead }) => {
  if (loading) return <div className="flex justify-center py-10"><RefreshCw size={24} className="animate-spin" style={{ color: '#F5831F' }} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: '#666' }}>{unread} unread notification{unread !== 1 ? 's' : ''}</p>
        {unread > 0 && (
          <button onClick={onMarkAllRead} className="text-xs font-medium px-3 py-1 rounded-full" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }} data-testid="mark-all-read">
            Mark all as read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white rounded-lg p-10 text-center" style={{ border: '1px solid #E0E0E0' }}>
          <Bell size={40} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
          <p className="text-sm" style={{ color: '#666' }}>No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n, i) => (
            <div
              key={n.notification_id || i}
              className="bg-white rounded-lg p-4 transition-all"
              style={{
                border: `1px solid ${n.read ? '#E0E0E0' : '#F5831F'}`,
                borderLeftWidth: n.read ? 1 : 3,
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: n.read ? '#F3F4F6' : '#F5831F15' }}>
                  <Bell size={14} style={{ color: n.read ? '#999' : '#F5831F' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: '#1A1209' }}>{n.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#666' }}>{n.message}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {n.sender_name && <span className="text-xs" style={{ color: '#F5831F' }}>{n.sender_name}</span>}
                    <span className="text-xs" style={{ color: '#999' }}>{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Activity Timeline Tab ──────────────────────────────────────────────────

const EVENT_CONFIG = {
  created: { icon: Bug, color: '#F5831F', label: 'Created' },
  status_changed: { icon: RefreshCw, color: '#2563EB', label: 'Status Changed' },
  escalated: { icon: ArrowUp, color: '#DC2626', label: 'Escalated' },
  responded: { icon: MessageSquare, color: '#2EAA5C', label: 'Response' },
  priority_changed: { icon: AlertTriangle, color: '#F59E0B', label: 'Priority Changed' },
};

const ActivityTimelineTab = ({ events, loading, onRefresh, onFilter }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Trigger filter on backend
  useEffect(() => {
    const params = {};
    if (eventFilter !== 'all') params.event_type = eventFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    onFilter(params);
    // Same rationale as the search effect above — `onFilter` is a
    // parent callback whose reference may flip per render, but the
    // effect body is a single idempotent notification so re-firing is
    // safe (the parent handler owns dedup / cancellation).
  }, [eventFilter, debouncedSearch, onFilter]);

  // Group events by date
  const grouped = {};
  (events || []).forEach(ev => {
    const d = new Date(ev.timestamp).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(ev);
  });

  const EVENT_TYPES = [
    { id: 'all', label: 'All Events' },
    { id: 'created', label: 'Created' },
    { id: 'status_changed', label: 'Status Changes' },
    { id: 'escalated', label: 'Escalations' },
    { id: 'responded', label: 'Responses' },
    { id: 'priority_changed', label: 'Priority Changes' },
  ];

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Search & Filter Bar */}
      <div className="bg-white rounded-lg p-4 space-y-3" style={{ border: '1px solid #E0E0E0' }}>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#999' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by ticket ID, actor, or description..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: '#E0E0E0' }}
            data-testid="timeline-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <XCircle size={14} style={{ color: '#999' }} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {EVENT_TYPES.map(et => (
            <button
              key={et.id}
              onClick={() => setEventFilter(et.id)}
              className="px-3 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: eventFilter === et.id ? '#F5831F' : '#F3F4F6',
                color: eventFilter === et.id ? '#FFF' : '#666',
              }}
              data-testid={`timeline-filter-${et.id}`}
            >
              {et.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline Content */}
      {loading ? (
        <div className="flex justify-center py-10"><RefreshCw size={24} className="animate-spin" style={{ color: '#F5831F' }} /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-lg p-10 text-center" style={{ border: '1px solid #E0E0E0' }}>
          <GitCommit size={40} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
          <p className="text-sm" style={{ color: '#666' }}>
            {searchQuery || eventFilter !== 'all' ? 'No events match your filter' : 'No activity yet'}
          </p>
        </div>
      ) : (
        Object.entries(grouped).map(([dateStr, dayEvents]) => (
          <div key={dateStr}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#999' }}>{dateStr}</span>
              <div className="flex-1 h-px" style={{ backgroundColor: '#E0E0E0' }} />
            </div>

            <div className="relative pl-6">
              <div className="absolute left-[11px] top-0 bottom-0 w-px" style={{ backgroundColor: '#E0E0E0' }} />

              {dayEvents.map((ev, i) => {
                const cfg = EVENT_CONFIG[ev.event_type] || EVENT_CONFIG.created;
                const Icon = cfg.icon;
                return (
                  <div key={ev.event_id || i} className="relative mb-4 last:mb-0">
                    <div className="absolute -left-6 top-1 w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15`, border: `2px solid ${cfg.color}` }}>
                      <Icon size={10} style={{ color: cfg.color }} />
                    </div>

                    <div className="bg-white rounded-lg p-3 ml-2 transition-all hover:shadow-sm" style={{ border: '1px solid #E0E0E0' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                          {cfg.label}
                        </span>
                        <span className="font-mono text-xs" style={{ color: '#999' }}>{ev.ticket_id}</span>
                        <span className="text-xs" style={{ color: '#999' }}>
                          {new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm mt-1" style={{ color: '#333' }}>{ev.detail}</p>
                      <p className="text-xs mt-1" style={{ color: '#999' }}>
                        by <span style={{ color: '#F5831F' }}>{ev.actor_name}</span>
                        {ev.metadata?.title && <> &middot; {ev.metadata.title}</>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('health');
  const [health, setHealth] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [timeline, setTimeline] = useState([]);
  const [digestSending, setDigestSending] = useState(false);
  const [loading, setLoading] = useState({ health: true, tickets: true, notifs: true, timeline: true });

  const fetchHealth = useCallback(async () => {
    setLoading(p => ({ ...p, health: true }));
    try {
      const data = await apiClient.request('/api/developer/system-health');
      setHealth(data);
    } catch (e) { console.error('Health fetch error:', e); }
    setLoading(p => ({ ...p, health: false }));
  }, []);

  const fetchTickets = useCallback(async (search = '') => {
    setLoading(p => ({ ...p, tickets: true }));
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await apiClient.request(`/api/developer/tickets${qs}`);
      setTickets(data.tickets || []);
      setTicketStats(data.stats || {});
    } catch (e) { console.error('Tickets fetch error:', e); }
    setLoading(p => ({ ...p, tickets: false }));
  }, []);

  const fetchNotifs = useCallback(async () => {
    setLoading(p => ({ ...p, notifs: true }));
    try {
      const data = await apiClient.request('/api/developer/notifications');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) { console.error('Notifs fetch error:', e); }
    setLoading(p => ({ ...p, notifs: false }));
  }, []);

  const fetchTimeline = useCallback(async (params = {}) => {
    setLoading(p => ({ ...p, timeline: true }));
    try {
      const qs = new URLSearchParams({ limit: '100', ...params }).toString();
      const data = await apiClient.request(`/api/developer/activity-timeline?${qs}`);
      setTimeline(data.events || []);
    } catch (e) { console.error('Timeline fetch error:', e); }
    setLoading(p => ({ ...p, timeline: false }));
  }, []);

  useEffect(() => {
    fetchHealth();
    fetchTickets();
    fetchNotifs();
    fetchTimeline();
  }, [fetchHealth, fetchTickets, fetchNotifs, fetchTimeline]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'health') fetchHealth();
      if (activeTab === 'tickets') fetchTickets();
      if (activeTab === 'notifications') fetchNotifs();
      if (activeTab === 'activity') fetchTimeline();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, fetchHealth, fetchTickets, fetchNotifs, fetchTimeline]);

  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await apiClient.request(`/api/developer/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      fetchTickets();
    } catch (e) { console.error('Update error:', e); }
  };

  const handleRespond = async (ticketId, message) => {
    try {
      await apiClient.request(`/api/developer/tickets/${ticketId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      fetchTickets();
    } catch (e) { console.error('Respond error:', e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiClient.request('/api/developer/notifications/mark-read', { method: 'POST' });
      fetchNotifs();
    } catch (e) { console.error('Mark read error:', e); }
  };

  const handleSendDigest = async () => {
    setDigestSending(true);
    try {
      await apiClient.request('/api/developer/digest/send', { method: 'POST' });
    } catch (e) { console.error('Digest error:', e); }
    setDigestSending(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('hibff_auth_token');
    sessionStorage.removeItem('hibff_auth_token');
    navigate('/login');
  };

  const tabs = [
    { id: 'health', label: 'System Health', icon: Activity },
    { id: 'stabilisation', label: 'Stabilisation', icon: FileText },
    { id: 'tickets', label: 'Tickets', icon: Bug, badge: ticketStats?.open },
    { id: 'activity', label: 'Activity', icon: GitCommit },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
  ];

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F9F9F9' }}>
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full flex flex-col" style={{ width: '220px', backgroundColor: '#1A1209', zIndex: 40 }}>
        <div className="px-5 py-5 border-b border-white/10">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-10 cursor-pointer" onClick={() => navigate('/developer')} />
          <p className="text-xs mt-1" style={{ color: '#F5831F' }}>Developer Console</p>
        </div>

        <nav className="flex-1 py-4">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors relative"
                style={{
                  color: active ? '#F5831F' : '#9CA3AF',
                  backgroundColor: active ? 'rgba(245,131,31,0.08)' : 'transparent',
                }}
                data-testid={`dev-nav-${tab.id}`}
              >
                {active && <span className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r" style={{ backgroundColor: '#F5831F' }} />}
                <Icon size={16} />
                <span className="font-medium">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#DC2626', minWidth: 20, textAlign: 'center' }}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="px-5 py-4 border-t border-white/10">
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="dev-logout">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1" style={{ marginLeft: '220px' }}>
        <header className="sticky top-0 bg-white flex items-center justify-between px-6 py-3" style={{ borderBottom: '1px solid #E0E0E0', zIndex: 30 }}>
          <h1 className="text-lg font-bold" style={{ color: '#1A1209' }}>
            {tabs.find(t => t.id === activeTab)?.label || 'Developer Dashboard'}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (activeTab === 'health') fetchHealth();
                if (activeTab === 'tickets') fetchTickets();
                if (activeTab === 'notifications') fetchNotifs();
                if (activeTab === 'activity') fetchTimeline();
              }}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} style={{ color: '#666' }} />
            </button>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: '#7B4FD8' }}>
              DEV
            </div>
          </div>
        </header>

        <main className="p-6">
          {activeTab === 'health' && <SystemHealthTab health={health} loading={loading.health} onRefresh={fetchHealth} onSendDigest={handleSendDigest} digestSending={digestSending} />}
          {activeTab === 'stabilisation' && <StabilisationLog />}
          {activeTab === 'tickets' && (
            <TicketsTab
              tickets={tickets}
              stats={ticketStats}
              loading={loading.tickets}
              onRefresh={fetchTickets}
              onUpdateTicket={handleUpdateTicket}
              onRespond={handleRespond}
              onSearch={(q) => fetchTickets(q)}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsTab
              notifications={notifications}
              unread={unreadCount}
              loading={loading.notifs}
              onMarkAllRead={handleMarkAllRead}
            />
          )}
          {activeTab === 'activity' && (
            <ActivityTimelineTab
              events={timeline}
              loading={loading.timeline}
              onRefresh={fetchTimeline}
              onFilter={(params) => fetchTimeline(params)}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default DeveloperDashboard;
